/**
 * Payment and donation lifecycles, per SPEC.md §10.5 and docs/domain-model.md.
 */
import { StateMachine } from "./state-machine.js";
export const PAYMENT_ATTEMPT_STATES = [
    "created",
    "requires_action",
    "processing",
    /*
     * DEC-056 (owner, 2026-10-17): an organization may capture card gifts at the end of its day. The
     * card is authorized (Stripe `requires_capture`) and nothing is taken until the capture job runs,
     * so the donor's bank holds the money and a staff member may still cancel at no cost. Never
     * "received": no ledger entry, no receipt, and the public total shows it as processing.
     */
    "authorized",
    "succeeded",
    "settled",
    /*
     * GH-950. The bank refused one try, and the intent is still open: Stripe fires
     * `payment_intent.payment_failed` on **every** declined confirmation, returns the intent to
     * `requires_payment_method`, and lets the donor try another card in the same PaymentSheet /
     * Elements form / Tap to Pay keyed sheet — and that same intent can then succeed. Only
     * `payment_intent.canceled` ends an intent at Stripe.
     *
     * Mapping a decline to `failed` (terminal) made the later success "illegal": the card was
     * charged and the gift was never booked, receipted, or counted. `declined` is not terminal, and
     * nothing counts it as money; see the machine below for where it may go.
     */
    "declined",
    "failed",
    "cancelled",
    "returned",
    "disputed",
    "refunded",
    "partially_refunded",
    "reversed",
];
export const paymentAttemptMachine = new StateMachine({
    name: "PaymentAttempt",
    initial: "created",
    progression: ["created", "requires_action", "processing", "authorized", "succeeded", "settled"],
    /*
     * `reversed` is **not** here, and that is the one change worth explaining.
     *
     * A chargeback lost and then won on appeal is a real, ordinary provider sequence. Losing it
     * moves the attempt to `reversed`; winning the appeal makes the money ours again and the
     * ledger says so with a reversal of the loss plus the release the win earns. Leaving
     * `reversed` terminal made the attempt the one party to that story that could not recover,
     * so a gift we kept read as money that went back out for ever.
     *
     * Every other exit state stays terminal: a failed card, a cancelled intent, a returned ACH
     * debit, and a refund are all decisions nobody appeals. `declined` (GH-950) is deliberately not
     * an exit state: a declined *try* is not a declined *intent*.
     */
    terminal: ["failed", "cancelled", "returned", "refunded"],
    transitions: {
        // `authorized` straight from `created`: a tapped card (and a wallet) is confirmed in one step,
        // and Stripe's first event for it is `amount_capturable_updated`.
        created: [
            "requires_action",
            "processing",
            "authorized",
            "succeeded",
            "declined",
            "failed",
            "cancelled",
        ],
        requires_action: ["processing", "authorized", "succeeded", "declined", "failed", "cancelled"],
        processing: ["authorized", "succeeded", "declined", "failed", "cancelled", "returned"],
        /*
         * DEC-056. Out of an authorization there are exactly three ways: the capture job (or a capture
         * anywhere) takes the money (`succeeded`), staff cancel it or Stripe lets it lapse
         * (`cancelled`), or the capture is refused (`failed`). A late `processing` is stale by rank.
         */
        authorized: ["succeeded", "declined", "failed", "cancelled"],
        /*
         * GH-950. Out of a declined try the donor may try again on the **same** intent: through 3-D
         * Secure (`requires_action`), into an ACH or wallet `processing`, onto an end-of-day hold
         * (`authorized`, DEC-056), or straight to `succeeded`. Only a cancellation — Stripe's
         * `payment_intent.canceled`, the GH-791 supersession, a staff cancel — or one of our own
         * local `failed` writes ends it. `declined -> declined` is a second decline: a duplicate by
         * state, which is all it is (the failure code of the first try stays on the row).
         */
        declined: ["requires_action", "processing", "authorized", "succeeded", "failed", "cancelled"],
        // A card charge can be disputed or refunded before the payout settles, so these are
        // reachable directly from `succeeded` and not only from `settled`.
        succeeded: ["settled", "refunded", "partially_refunded", "disputed", "returned", "reversed"],
        settled: ["refunded", "partially_refunded", "disputed", "returned", "reversed"],
        partially_refunded: ["refunded", "partially_refunded", "disputed", "reversed"],
        /*
         * `succeeded` is here because a **won** dispute has to have somewhere honest to go.
         *
         * Before it did not: the only forward move from `disputed` was `settled`, which asserts the
         * processor has paid the money out — a claim nobody has evidence for at the moment a
         * chargeback is decided in our favour. The alternative, leaving the attempt `disputed`, makes
         * `countsAsReceived` false forever for a gift we actually kept. `succeeded` says exactly what
         * is true after a win: the provider confirmed the money, and no payout is asserted.
         *
         * A dedicated `dispute_resolved` state was the other candidate and was rejected. It would
         * carry precisely the meaning `succeeded` already carries, while requiring the
         * `payment_attempts.state` CHECK to be widened by migration and every reader of the state
         * vocabulary — the daily close's confirmed/reversed buckets, donor history, the contracts
         * enums — to learn a synonym. A state machine change is only worth its blast radius when it
         * expresses something the existing vocabulary cannot.
         *
         * `succeeded → settled` remains open, so a genuine settlement arriving after the win still
         * lands; under the old `settled` choice that later event was a no-op duplicate.
         */
        disputed: ["refunded", "partially_refunded", "reversed", "settled", "succeeded"],
        // Terminal. A returned ACH debit or a failed attempt does not come back to life; a new
        // attempt is a new row, which is what keeps the audit trail honest. Since GH-950 no provider
        // event writes `failed` — a Stripe decline is `declined` — so `failed` is only ever written
        // locally, where nothing can confirm the intent again (the provider was unreachable, a saved
        // card was refused inside our own server-side confirm, a session expired).
        failed: [],
        cancelled: [],
        returned: [],
        refunded: [],
        /*
         * The single edge out of `reversed`: a dispute lost and then **won on appeal**.
         *
         * `succeeded` for the same reason the `disputed` edge above chose it — the provider has
         * confirmed the money and no payout is asserted. Not `disputed`, which would say the case is
         * still open; not `settled`, which would claim a payout nobody has evidence for.
         *
         * This is deliberately *not* a general resurrection. `reversed` is only ever written by the
         * lost-chargeback branch of the webhook handler, and the only caller that may ask for this
         * edge is the appeal branch of the same handler — `isDisputeAppealRecovery` names the pair so
         * the ordinary payment path can refuse it, and so any route that tries is one search away.
         * A redelivered `payment_intent.succeeded` landing on a reversed attempt is still rejected.
         */
        reversed: ["succeeded"],
    },
});
/**
 * The one edge out of `reversed`, named so it can be refused everywhere except where it belongs.
 *
 * Widening `reversed` to reach `succeeded` is what lets a won appeal put an attempt back on the
 * books. It is also, on its own, exactly the transition an attacker or a stale redelivery would
 * want: a `payment_intent.succeeded` arriving after a chargeback was lost would otherwise
 * resurrect a gift the issuer took back. The state machine cannot tell those two callers apart —
 * only the caller knows whether it is holding a won dispute — so the ordinary provider
 * payment-state path consults this predicate and rejects the transition it describes.
 */
export function isDisputeAppealRecovery(from, to) {
    return from === "reversed" && to === "succeeded";
}
/**
 * How a provider's payment event classifies against an attempt — the one classification the
 * webhook's ordinary payment path uses (GH-950).
 *
 * Two things on top of the machine:
 *
 * - The dispute-appeal edge is refused (see `isDisputeAppealRecovery`).
 * - A decline that cannot apply is **stale**, never illegal. Stripe delivers events out of order,
 *   and a declined first try followed by a successful retry on the same intent is an ordinary
 *   sequence; its `payment_failed` arriving after the `succeeded` (or after a cancel, or after our
 *   own local `failed`) says nothing about money and must not raise the illegal-transition alarm.
 *   The reverse is not softened: a success, authorization or processing after a terminal state
 *   is still illegal and still alerts.
 */
export function classifyProviderTransition(from, to) {
    if (isDisputeAppealRecovery(from, to))
        return "illegal";
    const outcome = paymentAttemptMachine.classify(from, to);
    return outcome === "illegal" && to === "declined" ? "stale" : outcome;
}
export const DONATION_STATES = [
    "draft",
    "processing",
    "completed",
    "failed",
    /*
     * DEC-056: a gift whose card authorization was cancelled before capture — by staff, or because
     * Stripe let it lapse. Nothing was ever taken. Distinct from `failed` (the bank said no) so the
     * console can say what happened, and terminal: a cancelled gift is never paid again on the same
     * quote. Donor-facing wires that shipped before it (the donation status `state`) map it to
     * `failed`, because those clients decode the enum strictly.
     */
    "cancelled",
    "returned",
    "refunded",
    "partially_refunded",
    "reversed",
];
export const donationMachine = new StateMachine({
    name: "Donation",
    initial: "draft",
    progression: ["draft", "processing", "completed"],
    /*
     * `reversed` is not terminal here for the same reason it is not terminal on the attempt: a
     * lost chargeback won on appeal has to be able to land on all three records that describe the
     * gift. When the ledger reversed the loss and the attempt went back to `succeeded` but the
     * donation stayed `reversed`, the donation row contradicted the money — and the public
     * `donation_count`, which is derived from this column, stayed one short for a gift the organization
     * still had.
     */
    terminal: ["returned", "refunded", "cancelled"],
    transitions: {
        // `cancelled` from `draft` too: a Tap to Pay authorization can be withdrawn on the phone
        // before Stripe's webhook has moved the gift to `processing` (DEC-056).
        draft: ["processing", "completed", "failed", "cancelled"],
        // `cancelled` otherwise only from `processing`: the donation stays `processing` while its
        // newest attempt is `authorized` (the attempt carries the authorization, not the gift).
        processing: ["completed", "failed", "returned", "cancelled"],
        completed: ["refunded", "partially_refunded", "returned", "reversed"],
        partially_refunded: ["refunded", "partially_refunded", "reversed"],
        /*
         * Back to `draft` when a donor whose card was declined pays the same gift another way
         * (GH-791). Before, a decline ended the gift and the donor was told their checkout had
         * "already been used" — the quote is bound to one donation, and that donation could never be
         * paid again. A *new attempt* follows, and its provider events move the gift on from `draft`.
         * Only the payment-intent path asks for this edge, and only after the provider has confirmed
         * no earlier attempt can still charge (a declined intent is cancelled at the provider first);
         * `isDonorRetry` names it so a webhook — which never has a reason to write `draft` — can be
         * refused it.
         *
         * GH-950: `processing` and `completed` as well, for one sender only — the webhook, when the
         * attempt that failed the gift was **declined** (not terminal) and the donor's retry on that
         * *same* intent then went through. The decline had marked the gift `failed`, and the money
         * Stripe then took has to complete it; refusing the edge is exactly the bug that left a
         * charged card with no booked gift. A terminal (`failed`, `cancelled`) attempt can never move
         * again, so it can never ask for these edges.
         */
        failed: ["draft", "processing", "completed"],
        cancelled: [],
        returned: [],
        refunded: [],
        /*
         * Won on appeal, and nothing else.
         *
         * `completed` rather than a new `reinstated` state, for the reason the attempt machine
         * already recorded when it rejected `dispute_resolved`: reinstatement means precisely what
         * `completed` already means — the gift stands — while a new value would need the
         * `donations.state` CHECK widened by migration and every reader of the vocabulary (the goal
         * projection's `donation_count`, tax reporting, donor history, the contracts enum) taught a
         * synonym for a word they already have. A state is worth its blast radius only when it says
         * something the existing vocabulary cannot.
         *
         * The ledger's own recovery is a reversal plus a replacement rather than an edit, and that
         * law is untouched: the loss transaction is still there with a reversal pointing at it. This
         * column is not a posted entry, it is the current status of the gift, and the current status
         * of a gift whose chargeback was overturned is that it completed.
         */
        reversed: ["completed"],
    },
});
/**
 * Whether a payment state contributes to the public "received" total (SPEC.md §7.2).
 *
 * Note that `succeeded` counts and `processing` does not. Everything reversed, returned,
 * refunded, failed, or cancelled is excluded — the thermometer must never show money that came
 * back out, and must never show money that has not yet gone in.
 */
/** The one edge back out of a failed donation, named so only the payment-intent path takes it. */
export function isDonorRetry(from, to) {
    return from === "failed" && to === "draft";
}
export function retryDisposition(state) {
    switch (state) {
        // GH-950: `declined` too — a declined intent can still be confirmed by a sheet the donor left
        // open, so it is cancelled at the provider before a new attempt opens, never treated as closed.
        case "created":
        case "requires_action":
        case "declined":
            return "supersede";
        case "failed":
        case "cancelled":
            return "closed";
        case "processing":
        case "authorized":
            return "in_flight";
        case "succeeded":
        case "settled":
        case "partially_refunded":
        case "refunded":
        case "returned":
        case "disputed":
        case "reversed":
            return "paid";
    }
}
export function countsAsReceived(state) {
    return state === "succeeded" || state === "settled" || state === "partially_refunded";
}
/**
 * Authorized but unconfirmed. Displayed separately and never merged into "received". An
 * end-of-day authorization (DEC-056) is processing until it is captured.
 */
export function countsAsProcessing(state) {
    return state === "processing" || state === "requires_action" || state === "authorized";
}
/** The SQL list behind `countsAsProcessing`, for the projections that sum it in the database. */
export const PROCESSING_ATTEMPT_STATES_SQL = "'processing', 'requires_action', 'authorized'";
/**
 * A client redirect can never move a payment to a received state (SPEC.md §5.4, §18.1). Only a
 * verified provider webhook or a reconciliation run may. This predicate is the single place that
 * rule is expressed, so any route that tries is one search away.
 */
export function isProviderConfirmedState(state) {
    return state !== "created" && state !== "requires_action";
}
//# sourceMappingURL=payment-states.js.map